import React from 'react';
import axios from 'axios';

// CSRF Token handling
export const getCsrfToken = () => {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
};

// Rate limiting check
export const checkRateLimit = async () => {
  try {
    const response = await axios.get('/api/rate-limit-check');
    return response.data.allowed;
  } catch (error) {
    console.error('Rate limit check failed:', error);
    return true; // Allow the request if rate limiting is not implemented
  }
};

// Session management
export const checkSession = async () => {
  try {
    // Check if user is logged in by attempting to access a protected endpoint
    const response = await axios.get('/api/check-auth', {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      timeout: 5000 // 5 second timeout
    });
    return response.data.authenticated;
  } catch (error) {
    // If the endpoint doesn't exist or returns 404, assume session is valid
    // This prevents blocking legitimate users when the endpoint isn't implemented
    if (error.response?.status === 404) {
      return true;
    }
    console.error('Session check failed:', error);
    return true; // Default to true to prevent blocking legitimate users
  }
};

// Input sanitization
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .replace(/data:/gi, ''); // Remove data: protocol
};

// Security logging (development only)
export const logSecurityEvent = (eventType, details) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('Security Event:', {
      type: eventType,
      details,
      timestamp: new Date().toISOString(),
      url: window.location.href
    });
  }
};

// Password strength checker
export const checkPasswordStrength = (password) => {
  const minLength = 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  const strength = {
    score: 0,
    feedback: []
  };

  if (password.length >= minLength) strength.score += 1;
  else strength.feedback.push('Password should be at least 8 characters long');

  if (hasUpperCase) strength.score += 1;
  else strength.feedback.push('Include uppercase letters');

  if (hasLowerCase) strength.score += 1;
  else strength.feedback.push('Include lowercase letters');

  if (hasNumbers) strength.score += 1;
  else strength.feedback.push('Include numbers');

  if (hasSpecialChar) strength.score += 1;
  else strength.feedback.push('Include special characters');

  return strength;
};

// Error boundary component
export class SecurityErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logSecurityEvent('ERROR_BOUNDARY', {
      error: error.toString(),
      errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h2>Something went wrong</h2>
          <p>Please try refreshing the page</p>
          <button onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
} 